export enum SentryTags {
  NetworkConnectionDownlink = 'NetworkConnection.downlink',
  NetworkConnectionEffectiveType = 'NetworkConnection.effectiveType',
  NetworkConnectionRtt = 'NetworkConnection.rtt',
  NetworkConnectionSaveData = 'NetworkConnection.saveData',
  NetworkConnectionType = 'NetworkConnection.type',

  /** prophecy.error.ws can be used to tag errors related to ProphecyWSErrorTags or lsp method */
  ProphecyErrorWSType = 'prophecy.error.ws',
  /** prophecy.error.gql can be used to tag errors related to graphql query name */
  ProphecyErrorGqlType = 'prophecy.error.gql',
  /** prophecy.error.http can be used to tag errors related to rest api */
  ProphecyErrorHttpType = 'prophecy.error.http',
  /** prophecy.error can be used to tag in-geneal errors
   * (which can't be covered with ProphecyErrorWSType/ProphecyErrorGqlType/ProphecyErrorHttpType) */
  ProphecyErrorType = 'prophecy.error',
  /** set when an ErrorBoundary fallback (crash screen) is shown to the user; value: 'default' | 'custom' */
  CrashScreenShown = 'prophecy.ui.crashScreen',
  /** was the page unloading when the request failed */
  PageUnloading = 'prophecy.network.pageUnloading',
  /** original DOM error name, which prophecyErrorToError flattens to Error */
  NetworkErrorName = 'prophecy.network.errorName',
  /** request path with ids stripped, to keep the tag's cardinality indexable */
  NetworkPath = 'prophecy.network.path',
  /** host of the failed request, which used to survive only in the message that split the grouping */
  NetworkHost = 'prophecy.network.host',
  /** HTTP method, so a failing POST can be told apart from a failing GET on the same path */
  NetworkMethod = 'prophecy.network.method',
  /** HTTP status of the failed response, which otherwise survives only inside the message text */
  NetworkStatus = 'prophecy.network.status',
  /** 'network' (no response arrived) or 'backend' (the server answered non-2xx) */
  ApiErrorKind = 'prophecy.api.errorKind',
  /** navigator.onLine at capture time */
  NetworkOnline = 'prophecy.network.online',
  /** document.visibilityState at capture time */
  PageVisibility = 'prophecy.ui.visibility',
  /** was the socket open when the error fired — separates live failures from teardown/reconnect races */
  WSConnected = 'prophecy.ws.connected',
  /** reconnect attempts so far, already computed in _onClose and otherwise discarded */
  WSRetryCount = 'prophecy.ws.retryCount',
  /** did this client ever complete a handshake — separates a refused connect from a mid-session drop */
  WSEverOpened = 'prophecy.ws.everOpened',
  /** RFC 6455 code of the most recent close; absent on the first connect attempt */
  WSCloseCode = 'prophecy.ws.closeCode',
  /** backend-vs-network call derived from the above; see `socketDisconnectReason` */
  WSDisconnectReason = 'prophecy.ws.disconnectReason',
  /** Sentry's original key-list description, kept when a rejected payload is retitled */
  RejectedPayloadKeys = 'prophecy.error.rejectedKeys',
  /** host and path of the socket, lifted out of the message before it is reduced to a path for grouping */
  WSUrl = 'prophecy.ws.url',
  /** which caller found the socket down; see `notConnectedError` for why it is a tag and not the message */
  WSNotConnectedSource = 'prophecy.ws.notConnectedSource',
  /** the group a runtime error was headed for before `captureException` let it keep its own stack */
  GroupAsDeclined = 'prophecy.groupAs.declined',
  /** which IDE page was listening when its socket closed */
  WSSurface = 'prophecy.ws.surface',
  /** what the page made of the close; see `getIdeDisconnectKind` */
  WSIdeDisconnect = 'prophecy.ws.ideDisconnect',
  // orchestration
  OrchestrationEnvironmentConnectionError = 'orchestration.environment.connection'
}

// list here tags specific to websocket (ProphecyErrorWSType)
export enum ProphecyWSErrorTags {
  LspDisconnect = 'ws.lsp.disconnect',
  ExecutionDisconnect = 'ws.execution.disconnect',
  LspChunkReassembly = 'ws.lsp.chunkReassembly',
  LspFrameProcessing = 'ws.lsp.frameProcessing',
  LspFrameParse = 'ws.lsp.frameParse',
  NotConnected = 'ws.notConnected'
}

// list here tags specific to http (ProphecyErrorHttpType)
export enum HTTPErrorTags {
  QueryError = 'http.query.error',
  SessionExpire = 'http.query.sessionExpire'
}

// list here tags specific to Gql (ProphecyErrorGqlType)
export enum GqlErrorTags {
  QueryError = 'gql.query.error'
}

// list here custom tags (ProphecyErrorType)
export enum ErrorTags {
  testSqlConnection = 'testSqlConnection',

  // Javascript error
  JavascriptError = 'javascriptError',

  // network/fetch failure (request didn't complete)
  NetworkError = 'networkError',

  SapAiTermsPdfExport = 'sapAiTermsPdfExport'
}

export type ErrorTagsType =
  | Record<SentryTags.ProphecyErrorType, ErrorTags>
  | Record<SentryTags.ProphecyErrorWSType, ProphecyWSErrorTags | string>
  | Record<SentryTags.ProphecyErrorHttpType, HTTPErrorTags | string>
  | Record<SentryTags.ProphecyErrorGqlType, GqlErrorTags | string>
  | Record<
      SentryTags.OrchestrationEnvironmentConnectionError,
      {
        fabricId: string;
        connection: object;
      }
    >;
